import { createSlice } from '@reduxjs/toolkit';
import { registerUser, loginUser, checkEmail, resetPassword } from '../thunks/authThunks';
import { initializeAnalytics } from '../../utils/analytics';

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  registrationSuccess: false,
  cookiePreferences: JSON.parse(localStorage.getItem('cookiePreferences')) || null,
  resetPasswordSuccess: false,
  resetPasswordError: null,
  locationError: null,
  passwordError: null,
  ageError: null,
  submissionError: null,
  isLogin: false
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      const { user, token } = action.payload;
      state.user = {
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName
      };
      state.token = token;
      state.isAuthenticated = true;
    },
    clearError: (state) => {
      state.error = null;
      state.locationError = null;
      state.passwordError = null;
      state.ageError = null;
      state.submissionError = null;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    resetRegistrationSuccess: (state) => {
      state.registrationSuccess = false;
    },
    setCookiePreferences: (state, action) => {
      state.cookiePreferences = action.payload;
      localStorage.setItem('cookiePreferences', JSON.stringify(action.payload));
      initializeAnalytics(action.payload.analytics);
    },
    setResetPasswordSuccess: (state, action) => {
      state.resetPasswordSuccess = action.payload;
      state.resetPasswordError = null;
    },
    setResetPasswordError: (state, action) => {
      state.resetPasswordError = action.payload;
      state.resetPasswordSuccess = false;
    },
    setAuthError: (state, action) => {
      state.error = action.payload;
    },
    setLocationError: (state, action) => {
      state.locationError = action.payload;
    },
    setPasswordError: (state, action) => {
      state.passwordError = action.payload;
    },
    setAgeError: (state, action) => {
      state.ageError = action.payload;
    },
    setSubmissionError: (state, action) => {
      state.submissionError = action.payload;
    },
    setIsLogin: (state, action) => {
      state.isLogin = action.payload;
    },
    setError: (state, action) => {
      state.error = typeof action.payload === 'string' 
        ? { message: action.payload }
        : action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Register cases
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.user = {
          id: action.payload.user.id,
          email: action.payload.user.email,
          firstName: action.payload.user.firstName,
          lastName: action.payload.user.lastName
        };
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.loading = false;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = {
          id: action.payload.user.id,
          email: action.payload.user.email,
          firstName: action.payload.user.firstName,
          lastName: action.payload.user.lastName
        };
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        if (action.payload?.type === 'auth/register/rejected' && action.payload?.emailExists) {
          state.isLogin = true;
          state.error = 'Email already registered. Please login instead.';
        } else {
          state.error = action.payload;
        }
      })
      .addCase(checkEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkEmail.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.exists) {
          state.isLogin = true;
          state.error = 'Email already exists. Please login instead.';
        }
      })
      .addCase(checkEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.resetPasswordError = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.resetPasswordSuccess = true;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.resetPasswordError = action.payload;
      });
  }
});

export const { 
  setCredentials,
  clearError, 
  logout, 
  resetRegistrationSuccess, 
  setCookiePreferences, 
  setResetPasswordSuccess, 
  setResetPasswordError,
  setAuthError,
  setLocationError,
  setPasswordError,
  setAgeError,
  setSubmissionError,
  setIsLogin,
  setError
} = authSlice.actions;

export default authSlice.reducer;